import { catalog } from '../core/catalog.js';
import { epg, toProgram } from '../core/epg.js';
import { device } from '../core/device.js';
import { t } from '../core/i18n.js';
import { escapeHtml, formatClock, cleanName, debounce, throttle } from '../core/utils.js';
import { pageHead, filterBar, empty, enhance, infiniteScroll, img, programMeta } from '../ui/render.js';
import { splitProgramDesc, programMetaText } from '../core/epg-desc.js';
import { createLiveFilters } from '../ui/live-filters.js';

/* Guía de TV (EPG) en parrilla: canales en filas y programas sobre una línea de tiempo.
   Ventana de unas horas desplazable; el programa en emisión se reproduce, los pasados con
   catch-up se ven desde el inicio y los futuros muestran su sinopsis. */

const CHUNK = 25;
const WINDOW_MS = 6 * 3600000;
const STEP_MS = 2 * 3600000;
const SLOT_MS = 30 * 60000;

function floorSlot(ts) {
    return Math.floor(ts / SLOT_MS) * SLOT_MS;
}

export default {
    path: '/guide/',
    nav: 'guide',

    render() {
        return `<div class="page" data-name="guide">
            <div class="page-content">
                <div class="page-sticky">
                ${pageHead({
                    title: t('guide.title'),
                    bar: true,
                    actions: `<a class="btn btn-icon" href="#" data-href="/live/" aria-label="${t('nav.live')}"><span class="mi">live_tv</span></a>`
                })}
                <div class="guide-toolbar" data-nav-row>
                    <button class="btn btn-icon" data-shift="-1" aria-label="${t('guide.earlier')}"><span class="mi">chevron_left</span></button>
                    <button class="btn" data-shift="0"><span class="mi">schedule</span>${t('guide.now')}</button>
                    <button class="btn btn-icon" data-shift="1" aria-label="${t('guide.later')}"><span class="mi">chevron_right</span></button>
                    <div class="guide-filter" data-filters></div>
                </div>
                <div class="guide-days" data-nav-row>
                    <div class="guide-day-tabs" data-days></div>
                    <button class="btn btn-icon" data-cal aria-label="${t('guide.calendar')}" title="${t('guide.calendar')}"><span class="mi">calendar_month</span></button>
                </div>
                <div class="guide-scrub" data-scrub><span class="guide-scrub-now" data-scrub-now></span><span class="guide-scrub-bubble" data-scrub-label></span><input type="range" min="0" max="1425" step="15" value="0" aria-label="${t('guide.scrub')}" data-scrub-input/></div>
                </div>
                <div class="guide" data-guide></div>
                <div data-sentinel style="height:1px"></div>
            </div>
        </div>`;
    },

    mount(page, route) {
        const root = page.el;
        const els = {
            filters: root.querySelector('[data-filters]'),
            days: root.querySelector('[data-days]'),
            scrub: root.querySelector('[data-scrub]'),
            scrubInput: root.querySelector('[data-scrub-input]'),
            scrubLabel: root.querySelector('[data-scrub-label]'),
            scrubNow: root.querySelector('[data-scrub-now]'),
            guide: root.querySelector('[data-guide]'),
            sentinel: root.querySelector('[data-sentinel]'),
            head: root.querySelector('.page-head')
        };
        const basePpm = device.isTV ? 5 : 4; // píxeles por minuto de partida
        let ppm = basePpm;
        let windowMs = WINDOW_MS;
        let width = Math.round((windowMs / 60000) * ppm);
        // La selección (ámbito, proveedores, categorías, país, calidad y orden) es la de TV en directo: lo que se elige allí es lo que sale aquí.
        const lf = createLiveFilters();
        const state = {
            start: floorSlot(Date.now()) - SLOT_MS,
            items: [],
            shown: 0
        };
        let rowsEl = null;

        const x = (ts) => Math.round(((ts - state.start) / 60000) * ppm);
        // La parrilla llena el ancho disponible: en un monitor ancho se ven más horas (nunca menos de seis) y el
        // ancho por minuto se ajusta para que la última media hora acabe justo en el borde.
        const measure = () => {
            const chW = parseFloat(getComputedStyle(els.guide).getPropertyValue('--guide-ch-w')) || 150;
            const available = els.guide.clientWidth - chW;
            if (available < 200) { ppm = basePpm; windowMs = WINDOW_MS; }
            else {
                const minutes = Math.max(WINDOW_MS / 60000, Math.ceil(available / basePpm / 30) * 30);
                windowMs = minutes * 60000;
                ppm = available / minutes;
            }
            width = Math.round((windowMs / 60000) * ppm);
        };

        // Al desplazar la parrilla, el texto de los bloques que quedan parcialmente bajo la columna
        // de canales se empuja para que el título siga leyéndose (como en las guías de TV).
        const alignTitles = () => {
            const left = els.guide.scrollLeft;
            els.guide.querySelectorAll('.guide-prog').forEach((b) => {
                const start = b.offsetLeft;
                const end = start + b.offsetWidth;
                b.style.paddingLeft = start < left && end > left + 40 ? (left - start + 10) + 'px' : '';
            });
        };
        els.guide.addEventListener('scroll', throttle(alignTitles, 80), { passive: true });

        const compute = () => { state.items = lf.items(); };

        const renderFilters = () => {
            const { chips } = lf.bar();
            els.filters.innerHTML = filterBar(chips);
            // Está en la misma fila que los botones de fecha: una sola fila para el mando (izquierda/derecha pasa de uno a otro).
            const row = els.filters.firstElementChild;
            if (row) { row.removeAttribute('data-nav-row'); row.removeAttribute('data-nav-reach'); }
        };

        const afterFilter = (what) => {
            if (what && what.unlocked) { refreshAll(); return; }
            renderFilters();
            renderAll();
            root.querySelector('.page-content').scrollTo({ top: 0 });
        };
        /* ---- Días y calendario ---- */
        const DAY = 86400000;
        const dayStartOf = (ts) => { const d = new Date(ts); d.setHours(0, 0, 0, 0); return d.getTime(); };
        const todayStart = () => dayStartOf(Date.now());
        /** Días que se pueden consultar: los que tiene la guía (hasta una semana atrás por el catch-up y dos hacia delante), y siempre hoy y mañana. */
        const dayRange = () => {
            const today = todayStart();
            const r = epg.range();
            let from = today;
            let to = today + DAY;
            if (r) { from = Math.min(from, Math.max(dayStartOf(r.min), today - 7 * DAY)); to = Math.max(to, Math.min(dayStartOf(r.max - 1), today + 13 * DAY)); }
            return { from, to };
        };
        const dayLabel = (ds) => {
            const today = todayStart();
            const diff = Math.round((ds - today) / DAY);
            if (diff === 0) return t('guide.today');
            if (diff === 1) return t('guide.tomorrow');
            if (diff === -1) return t('guide.yesterday');
            const d = new Date(ds);
            const wd = d.toLocaleDateString(undefined, { weekday: 'long' });
            const name = wd.charAt(0).toUpperCase() + wd.slice(1);
            return Math.abs(diff) <= 5 ? name : name + ' ' + d.getDate();
        };
        const renderDays = () => {
            const { from, to } = dayRange();
            const cur = dayStartOf(state.start);
            let html = '';
            for (let ds = from; ds <= to; ds += DAY) html += `<button type="button" class="guide-day ${ds === cur ? 'is-active' : ''}" data-day="${ds}">${escapeHtml(dayLabel(ds))}</button>`;
            els.days.innerHTML = html;
            const act = els.days.querySelector('.is-active');
            if (act && els.days.scrollTo) els.days.scrollTo({ left: Math.max(0, act.offsetLeft - 40) });
        };
        /** Salta a un día: hoy, en este momento; otro día, a la hora de más audiencia (20:00). */
        const goDay = (ds) => {
            state.start = ds === todayStart() ? floorSlot(Date.now()) - SLOT_MS : ds + 20 * 3600000;
            renderAll();
            if (ds === todayStart()) scrollToNow(); else els.guide.scrollLeft = 0;
        };

        /* ---- Barra del tiempo: arrastrar para ir a otra hora del día (como en las guías de las operadoras) ---- */
        const minutesOfDay = (ts) => Math.round((ts - dayStartOf(ts)) / 60000 / 15) * 15;
        const fmtMin = (m) => String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
        const placeBubble = (m) => {
            const pct = Math.max(0, Math.min(100, (m / 1425) * 100));
            els.scrubLabel.style.left = `calc(${pct}% + ${(0.5 - pct / 100) * 18}px)`;
            els.scrubLabel.textContent = fmtMin(m);
        };
        const renderScrub = () => {
            const m = Math.min(1425, minutesOfDay(state.start));
            els.scrubInput.value = String(m);
            placeBubble(m);
            const isToday = dayStartOf(state.start) === todayStart();
            els.scrubNow.hidden = !isToday;
            if (isToday) els.scrubNow.style.left = `calc(${Math.min(100, (minutesOfDay(Date.now()) / 1425) * 100)}% + ${(0.5 - minutesOfDay(Date.now()) / 1425) * 18}px)`;
        };
        els.scrubInput.addEventListener('input', () => placeBubble(Number(els.scrubInput.value)));
        els.scrubInput.addEventListener('change', () => {
            state.start = dayStartOf(state.start) + Number(els.scrubInput.value) * 60000;
            renderAll();
            els.guide.scrollLeft = 0;
        });

        const renderAll = () => {
            renderGrid(true);
            renderDays();
            renderScrub();
        };

        const monthNames = () => Array.from({ length: 12 }, (_, i) => new Date(2026, i, 1).toLocaleDateString(undefined, { month: 'long' }));
        /** Calendario de un mes: se pueden elegir los días que tiene la guía. */
        const openCalendar = () => {
            const { from, to } = dayRange();
            let view = new Date(dayStartOf(state.start));
            view.setDate(1);
            const popupId = 'gcal-' + Date.now().toString(36);
            const body = () => {
                const y = view.getFullYear();
                const m = view.getMonth();
                const first = new Date(y, m, 1);
                const lead = (first.getDay() + 6) % 7; // semana desde el lunes
                const days = new Date(y, m + 1, 0).getDate();
                const cur = dayStartOf(state.start);
                const today = todayStart();
                const head = Array.from({ length: 7 }, (_, i) => new Date(2026, 9, 5 + i).toLocaleDateString(undefined, { weekday: 'narrow' }));
                let cells = '';
                for (let i = 0; i < lead; i++) cells += '<span class="gcal-cell is-blank"></span>';
                for (let d = 1; d <= days; d++) {
                    const ds = new Date(y, m, d).getTime();
                    const ok = ds >= from && ds <= to;
                    cells += `<button type="button" class="gcal-cell ${ok ? '' : 'is-off'} ${ds === cur ? 'is-active' : ''} ${ds === today ? 'is-today' : ''}" ${ok ? `data-gcal-day="${ds}"` : 'disabled'}>${d}</button>`;
                }
                const name = monthNames()[m];
                return `<div class="gcal"><div class="gcal-head"><button type="button" class="btn btn-icon" data-gcal-nav="-1" aria-label="${escapeHtml(t('guide.earlier'))}"><span class="mi">chevron_left</span></button><b>${escapeHtml(name.charAt(0).toUpperCase() + name.slice(1) + ' ' + y)}</b><button type="button" class="btn btn-icon" data-gcal-nav="1" aria-label="${escapeHtml(t('guide.later'))}"><span class="mi">chevron_right</span></button></div><div class="gcal-week">${head.map((h) => `<span>${escapeHtml(h)}</span>`).join('')}</div><div class="gcal-grid">${cells}</div></div>`;
            };
            const popup = window.app.popup.create({
                content: `<div class="popup catpick-popup gcal-popup" id="${popupId}"><div class="page">
                    <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner"><div class="title">${escapeHtml(t('guide.calendar'))}</div><div class="right"><a href="#" class="link popup-close btn-icon" aria-label="${escapeHtml(t('common.close'))}"><span class="mi">close</span></a></div></div></div>
                    <div class="page-content" data-gcal-body></div></div></div>`,
                on: {
                    open(pp) {
                        const el = pp.$el[0];
                        const paint = () => { el.querySelector('[data-gcal-body]').innerHTML = body(); };
                        paint();
                        el.addEventListener('click', (e) => {
                            const nav = e.target.closest('[data-gcal-nav]');
                            if (nav) { view = new Date(view.getFullYear(), view.getMonth() + Number(nav.getAttribute('data-gcal-nav')), 1); paint(); return; }
                            const day = e.target.closest('[data-gcal-day]');
                            if (day) { popup.close(); goDay(Number(day.getAttribute('data-gcal-day'))); }
                        });
                    },
                    opened(pp) { if (device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(pp.$el[0]), 80); },
                    closed() {
                        try { popup.destroy(); } catch (e) { /* ignore */ }
                        const node = document.getElementById(popupId);
                        if (node && node.parentNode) node.parentNode.removeChild(node);
                    }
                }
            });
            popup.open();
        };

        const timeHeader = () => {
            const cells = [];
            for (let ts = state.start; ts < state.start + windowMs; ts += SLOT_MS) {
                cells.push(`<div class="guide-slot" style="left:${x(ts)}px;width:${Math.round((SLOT_MS / 60000) * ppm)}px">${formatClock(ts)}</div>`);
            }
            const now = Date.now();
            const marker = now >= state.start && now <= state.start + windowMs ? `<div class="guide-now" style="left:${x(now)}px"></div>` : '';
            return `<div class="guide-head"><div class="guide-corner">${new Date(state.start).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</div><div class="guide-timeline" style="width:${width}px">${cells.join('')}${marker}</div></div>`;
        };

        const canRec = () => !!(window.IPTV && window.IPTV.account && window.IPTV.account.canRecord);
        const rowHtml = (ch) => {
            const winEnd = state.start + windowMs;
            const now = Date.now();
            const progs = epg.programs(ch.epgId).filter((p) => p[1] > state.start && p[0] < winEnd).map(toProgram);
            let blocks = '';
            if (!progs.length) {
                blocks = `<div class="guide-prog is-empty" style="left:0;width:${width}px"><span class="guide-prog-title">${t('guide.noData')}</span></div>`;
            } else {
                blocks = progs.map((p) => {
                    const left = Math.max(0, x(p.start));
                    const right = Math.min(width, x(p.stop));
                    const w = Math.max(2, right - left);
                    const isNow = p.start <= now && p.stop > now;
                    const past = p.stop <= now;
                    const catchup = past && catalog.canCatchup(ch, p);
                    const cls = isNow ? 'is-now' : past ? (catchup ? 'is-catchup' : 'is-past') : '';
                    const rec = canRec() && p.start > now && w >= 90 ? `<button type="button" class="guide-rec" style="left:${left + w - 30}px" data-rec="${ch.id}:${p.start}" tabindex="-1" data-nav-skip aria-label="${escapeHtml(t('rec.schedule'))}" title="${escapeHtml(t('rec.schedule'))}"><span class="mi">fiber_manual_record</span></button>` : '';
                    return `<button class="guide-prog ${cls}" style="left:${left}px;width:${w}px" data-prog="${ch.id}:${p.start}" title="${escapeHtml(p.title)}">
                        <span class="guide-prog-title">${escapeHtml(p.title)}</span>
                        <span class="guide-prog-time">${formatClock(p.start)}${isNow && w >= 110 ? ` <span class="guide-live">${escapeHtml(t('guide.live'))}</span>` : ''}${catchup ? ' <span class="mi">history</span>' : ''}</span>
                    </button>${rec}`;
                }).join('');
            }
            const now2 = Date.now();
            const marker = now2 >= state.start && now2 <= winEnd ? `<div class="guide-now" style="left:${x(now2)}px"></div>` : '';
            return `<div class="guide-row" data-ch="${ch.id}">
                <button class="guide-ch" data-play-live="${ch.id}" title="${escapeHtml(cleanName(ch.name))}">
                    <span class="guide-ch-logo">${ch.logo ? img(ch.logo, '') : ''}</span>
                    <span class="guide-ch-name">${escapeHtml(cleanName(ch.name))}</span>
                </button>
                <div class="guide-progs" data-nav-row style="width:${width}px">${blocks}${marker}</div>
            </div>`;
        };

        const renderGrid = (reset) => {
            if (reset) measure();
            compute();
            if (reset) {
                state.shown = 0;
                if (!catalog.loaded) {
                    els.guide.innerHTML = '';
                    return;
                }
                if (!state.items.length) {
                    els.guide.innerHTML = empty({ title: t('common.noResults'), text: t('common.noResultsHint') });
                    rowsEl = null;
                    return;
                }
                els.guide.innerHTML = `${timeHeader()}<div class="guide-rows" data-rows></div>${!epg.hasData() ? `<div class="guide-loading"><div class="spinner"></div>${t('live.epgLoading')}</div>` : ''}`;
                rowsEl = els.guide.querySelector('[data-rows]');
            }
            if (!rowsEl) return;
            const next = state.items.slice(state.shown, state.shown + CHUNK);
            state.shown += next.length;
            rowsEl.insertAdjacentHTML('beforeend', next.map(rowHtml).join(''));
            enhance(rowsEl);
        };

        const refreshAll = () => {
            if (!catalog.loaded) return;
            renderFilters();
            renderAll();
        };

        const scrollToNow = () => {
            const scroller = els.guide;
            const nowX = x(Date.now());
            scroller.scrollLeft = Math.max(0, nowX - 120);
            setTimeout(alignTitles, 0);
        };

        /** Programa una grabación del programa (con los márgenes del plan). */
        const recordProgram = async (ch, p) => {
            const account = window.IPTV.account;
            const L = account.recordings;
            try {
                await account.scheduleRecording({ channelId: ch.id, channelName: cleanName(ch.name), url: catalog.rawLiveUrl(ch), title: p.title, startAt: p.start - (L.startMarginSec || 0) * 1000, endAt: p.stop + (L.endMarginSec || 0) * 1000 });
                window.IPTV.notify.toast(t('rec.scheduled', { title: p.title, time: formatClock(p.start) }), { icon: 'event_available' });
            } catch (e) {
                window.IPTV.notify.error(t('rec.error.' + (e.code || 'generic')));
            }
        };

        const openProgram = (chId, start) => {
            const ch = catalog.get('live', chId);
            if (!ch) return;
            const p = epg.programs(ch.epgId).map(toProgram).find((q) => q.start === start);
            if (!p) return window.IPTV.actions.playLive(chId, { list: state.items });
            const now = Date.now();
            if (p.start <= now && p.stop > now) return window.IPTV.actions.playLive(chId, { list: state.items });
            if (p.stop <= now && catalog.canCatchup(ch, p)) {
                window.IPTV.player.open({ mode: 'live', item: ch, list: state.items, catchup: p });
                return;
            }
            const app = window.app;
            const buttons = [{ text: t('common.close') }];
            const account = window.IPTV.account;
            if (account.canRecord && p.stop > now) {
                buttons.push({ text: t('rec.schedule'), onClick: () => recordProgram(ch, p) });
            }
            if (p.start > now) buttons.push({ text: t('guide.watchChannel'), bold: true, onClick: () => window.IPTV.actions.playLive(chId, { list: state.items }) });
            app.dialog.create({
                cssClass: 'guide-dialog',
                title: escapeHtml(p.title),
                text: (() => { const d = splitProgramDesc(p.desc); return `<div class="guide-dlg-head"><b>${escapeHtml(cleanName(ch.name))}</b> · ${formatClock(p.start)} – ${formatClock(p.stop)}</div>${d.hasMeta ? programMeta(d) : ''}${d.plot ? `<p class="guide-dlg-plot">${escapeHtml(d.plot)}</p>` : ''}`; })(),
                buttons
            }).open();
        };

        root.addEventListener('click', (e) => {
            const rec = e.target.closest('[data-rec]');
            if (rec) {
                e.preventDefault();
                e.stopPropagation();
                const [chId, start] = rec.getAttribute('data-rec').split(':');
                const ch = catalog.get('live', chId);
                const p = ch && epg.programs(ch.epgId).map(toProgram).find((q) => q.start === Number(start));
                if (ch && p) recordProgram(ch, p);
                return;
            }
            const dayBtn = e.target.closest('[data-day]');
            if (dayBtn) { goDay(Number(dayBtn.getAttribute('data-day'))); return; }
            if (e.target.closest('[data-cal]')) { openCalendar(); return; }
            const prog = e.target.closest('[data-prog]');
            if (prog) {
                e.preventDefault();
                const [chId, start] = prog.getAttribute('data-prog').split(':');
                openProgram(chId, Number(start));
                return;
            }
            const shift = e.target.closest('[data-shift]');
            if (shift) {
                const dir = Number(shift.getAttribute('data-shift'));
                state.start = dir === 0 ? floorSlot(Date.now()) - SLOT_MS : state.start + dir * STEP_MS;
                renderAll();
                if (dir === 0) scrollToNow(); else els.guide.scrollLeft = 0;
                return;
            }
            const filter = e.target.closest('[data-filter]');
            if (filter) lf.open(filter.getAttribute('data-filter'), afterFilter);
        });

        const stopInfinite = infiniteScroll(els.sentinel, () => {
            if (catalog.loaded && state.shown < state.items.length) renderGrid(false);
        });

        refreshAll();
        scrollToNow();
        if (catalog.loaded && !epg.hasData() && catalog.profile) catalog.loadEpg().catch(() => {});
        if (device.isTV) {
            setTimeout(() => {
                const nowEl = root.querySelector('.guide-prog.is-now') || root.querySelector('.guide-prog');
                if (nowEl) window.IPTV.tvnav.focus(nowEl); else window.IPTV.tvnav.focusLayer(root);
            }, 200);
        }

        const tick = setInterval(() => {
            root.querySelectorAll('.guide-now').forEach((m) => { m.style.left = x(Date.now()) + 'px'; });
            renderScrub();
        }, 60000);
        // Al cambiar el tamaño de la ventana (o girar el móvil) se recalcula cuántas horas caben.
        const onResize = debounce(() => { if (!catalog.loaded) return; const before = width; measure(); if (width !== before) { renderGrid(true); scrollToNow(); } }, 250);
        // La parrilla se mide también cuando la página termina de colocarse (al entrar aún no tiene su ancho final).
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
        if (ro) ro.observe(els.guide); else window.addEventListener('resize', onResize);
        // Al volver a esta pantalla (la selección pudo cambiarse en TV en directo) se lee de nuevo y se repinta.
        const onIn = (e) => { if (e.target === root) { lf.reload(); refreshAll(); } };
        root.addEventListener('page:beforein', onIn);
        const off = [
            catalog.on('change', debounce(refreshAll, 300)),
            epg.on('change', debounce(() => { refreshAll(); scrollToNow(); }, 300))
        ];
        return () => {
            clearInterval(tick);
            stopInfinite();
            if (ro) ro.disconnect(); else window.removeEventListener('resize', onResize);
            root.removeEventListener('page:beforein', onIn);
            off.forEach((fn) => fn());
        };
    }
};
